"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Droplets, PhoneCall, Pipette, Search, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  iconName: string;
  highlight1: string;
  highlight2: string;
  highlight3: string;
  linkPath: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  badge: string;
}
const servicesList: ServiceItem[] = [{
  id: "drain-cleaning",
  title: "Drain Cleaning",
  description: "Fast clearing for stubborn clogs, slow drains, and recurring sink or main sewer line backups.",
  iconName: "pipette",
  highlight1: "Hydro-jetting & snaking",
  highlight2: "Camera line verification",
  highlight3: "Emergency same-day dispatch",
  linkPath: "/services/",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png-wvc-srcset",
  imageSizes: "(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 400px",
  imageAlt: "Plumber clearing a clogged pipe with professional drain cleaning equipment",
  badge: "Most Requested"
}, {
  id: "sewer-repair",
  title: "Sewer Line Repair",
  description: "Accurate diagnostics and trenchless repairs for cracked, broken, or tree-root-damaged underground sewer lines.",
  iconName: "droplets",
  highlight1: "Trenchless pipe lining",
  highlight2: "HD sewer camera inspection",
  highlight3: "Root intrusion removal",
  linkPath: "/services/",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair.png-wvc-srcset",
  imageSizes: "(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 400px",
  imageAlt: "Technician inspecting underground residential sewer pipes in Jonesboro",
  badge: "Urgent Care"
}, {
  id: "leak-detection",
  title: "Leak Detection & Repair",
  description: "Non-invasive electronic leak detection to pinpoint hidden slab leaks, wall moisture, and pipe bursts.",
  iconName: "search",
  highlight1: "Acoustic & thermal sensors",
  highlight2: "Zero-damage pinpointing",
  highlight3: "Slab & pipe burst repair",
  linkPath: "/services/",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_inspection.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_inspection.png-wvc-srcset",
  imageSizes: "(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 400px",
  imageAlt: "Plumber using electronic leak detection equipment to locate hidden water leak",
  badge: "Prevent Damage"
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const renderIcon = (name: string) => {
    switch (name) {
      case "pipette":
        return <Pipette className="size-5 text-primary" />;
      case "droplets":
        return <Droplets className="size-5 text-primary" />;
      case "search":
        return <Search className="size-5 text-primary" />;
      default:
        return <ShieldCheck className="size-5 text-primary" />;
    }
  };
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-3 font-semibold tracking-wide uppercase text-xs">
              Emergency & Maintenance Solutions
            </Badge>
            <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Related Plumbing Services in Jonesboro
            </h2>
            <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
              When plumbing emergencies strike, fast and accurate service prevents structural damage. Explore our core services backed by 24/7 dispatch across Jonesboro and nearby areas.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" asChild className="rounded-md">
              <Link to="/services/">
                View All Services
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesList.map((service, i) => <motion.div key={service.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: i * 0.12,
          ease: "easeOut"
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-sm hover:shadow-md transition-shadow bg-card text-card-foreground rounded-xl overflow-hidden">
                <div className="relative aspect-video w-full overflow-hidden bg-muted">
                  <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  <div className="absolute top-3 right-3">
                    <Badge variant="default" className="bg-primary text-primary-foreground shadow-sm">
                      {service.badge}
                    </Badge>
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <CardHeader className="pt-6 pb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                          {renderIcon(service.iconName)}
                        </div>
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Jonesboro, GA
                        </span>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-bold font-default">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 pb-4">
                      <div className="border-t border-border pt-4 mt-2">
                        <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                          Key Capabilities:
                        </p>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                            <span>{service.highlight1}</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                            <span>{service.highlight2}</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                            <span>{service.highlight3}</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 pb-6 border-t border-border flex items-center justify-between gap-3">
                    <Button variant="default" asChild className="w-full rounded-md font-medium">
                      <Link to={service.linkPath}>
                        Explore Service
                        <ArrowRight className="size-4 ml-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </div>
              </Card>
            </motion.div>)}
        </div>

        <div className="mt-12 rounded-xl bg-secondary p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 border border-border">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <PhoneCall className="size-6" />
            </div>
            <div>
              <h3 className="font-default text-lg font-bold text-foreground">
                Need urgent plumbing assistance in Jonesboro?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Our emergency team is on standby 24 hours a day, 7 days a week.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button variant="default" asChild className="w-full sm:w-auto font-medium rounded-md">
              <Link to="/contact/">
                Request Immediate Help
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}